import { MyUser,
  FileUploadUrl,
  ConnectorProfile,
  InvestorProfile,
  UpdateConnectorDealBody,
  GetEntProfileResponse,
  EntrepreneurUpdateBody,
  ConnectorDealsResponseType,
  ConnectorDealStatus,
  InvestorUpdateBody,
  ConnectorSendReferralRequest,
  InvestorReferralReplyBody,
  ConnectorReferralOption,
  ConnectorDeal,
  GetMatchedInvestorsForEntreprepeur,
  EntrepreneurAskReferralRequest,
  ConnectorNetworkResponseType,
  AddInvestorToNetworkResponse,
  EntrepreneurProfileBody,
  SendReferralsResponse,
  FilterOption,
  ReferralStatus,
  EntMatchedInvestorsResponse,
  MatchedInvestorListMetadataTabInput } from '../types/types';

const API_URL: string = process.env.REACT_APP_API_URL!;

export const InternalError: ApiError = {
  code: '500',
  message: 'Internal Service Error'
};
export interface ApiError {
  message: string,
  code: string
}

export enum ApiStatus {
  failed, success, loading
}

export interface CreateUserPostBody {
  firstName: string
  lastName: string
  email: string
  role: string
}

interface MatchedInvestorMetadataArgs {
  entId: number
  requested: MatchedInvestorListMetadataTabInput,
  recommended: MatchedInvestorListMetadataTabInput,
  sent: MatchedInvestorListMetadataTabInput
}

interface InviteEntRequestBody {
  entEmails: string[]
  inviteMessage: string
}

export interface InviteEntRequestResponse {
  sentInvites: string[],
  invitesToUnpublishedEnts: string[],
  dealsWithPublishedEnts: string[],
  nonEntExistingUserErrors: string[]
}

export interface Referral {
  referralId: number
  dealId: number
  status: ReferralStatus
  investorProfileId: number
  entProfileId: number
  investorName: string
  investorCompany: string
  entCompany: string
  lastUpdated: string
}

type ReferralRequestResponse = { referrals: Referral[], csv: string, moreResults: boolean };

export const isApiError = (error: any) => (error instanceof Object && error && 'message' in error && 'code' in error);

const apiErrorToThrow = (error: any): ApiError => {
  if (isApiError(error)) {
    return error;
  } else {
    console.error('An endpoint threw and did not provide a well formed ApiError');
    return InternalError;
  }
};

interface ReferralsRequestParams {
  status?: ReferralStatus
  daysBack?: number
}

const getReferrals = async (auth0AccessToken: string, requestParams: ReferralsRequestParams): Promise<ReferralRequestResponse> => {
  try {
    const { status, daysBack } = requestParams;
    let queryString = daysBack ? `?daysBack=${daysBack}` : '';

    if (status !== undefined) {
      if (queryString.length > 0) {
        queryString += `&status=${status}`;
      } else {
        queryString += `?status=${status}`;
      }
    }

    const ENDPOINT = `${API_URL}/connector/referrals${queryString}`;
    const response = await fetch(ENDPOINT, {
      headers: {
        Authorization: `Bearer ${auth0AccessToken}`
      }
    });

    const json = await response.json();

    if (!response.ok) {
      throw apiErrorToThrow(json);
    }

    return json;
  } catch (error: unknown) {
    throw apiErrorToThrow(error);
  }
};

class API {
  auth0AccessToken?: string;

  async getUser(auth0AccessToken: string): Promise<MyUser> {
    try {
      const ENDPOINT = `${API_URL}/user/me`;

      const response = await fetch(ENDPOINT, {
        headers: {
          Authorization: `Bearer ${auth0AccessToken}`
        }
      });

      const json = await response.json();

      if (!response.ok) {
        throw apiErrorToThrow(json);
      }

      const returnUser: MyUser = {
        id: json.id,
        name: `${json.firstName} ${json.lastName}`,
        role: json.role,
        email: json.email,
        auth0Id: json.auth0_id,
        profileId: json.profileId
      };

      return returnUser;
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }

  // returns true if user was created.
  async createEntUser(auth0AccessToken: string, body: CreateUserPostBody): Promise<true> {
    try {
      const ENDPOINT = `${API_URL}/user/create`;

      const response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${auth0AccessToken}`
        },
        body: JSON.stringify(body)
      });

      const json = await response.json();

      if (!response.ok) {
        throw apiErrorToThrow(json);
      }

      return true;
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }

  async updateEntrepreneurProfile(auth0AccessToken: string, body: EntrepreneurUpdateBody) : Promise<true> {
    try {
      const ENDPOINT = `${API_URL}/entrepreneur/profile`;
      if ((body.companyDetails) && (typeof body.companyDetails.employeeCount !== 'number')) {
        body.companyDetails.employeeCount = undefined;
      }
      const response = await fetch(ENDPOINT, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${auth0AccessToken}`
        },
        body: JSON.stringify(body)
      });

      const json = await response.json();

      if (!response.ok) {
        throw apiErrorToThrow(json);
      }

      return true;
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }

  // If the user is authenticated, use this to get ent profile.
  async getEntrepreneurProfile(auth0AccessToken: string, profileId: number, permalinkId?: string): Promise<{ profile: EntrepreneurProfileBody }> {
    try {
      let ENDPOINT = `${API_URL}/entrepreneur/profile/${profileId}`;

      if (permalinkId) {
        const params = new URLSearchParams({ permalinkId });
        ENDPOINT += `?${params}`;
      }

      const response = await fetch(ENDPOINT, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${auth0AccessToken}`
        }
      });

      const json = await response.json();

      if (!response.ok) {
        throw apiErrorToThrow(json);
      }

      const refData = json as GetEntProfileResponse;
      const { profile } = refData;
      if (!profile) {
        console.error('Profile was not available on successful response from getEnt endpoint.');
        throw InternalError;
      }
      return { profile };
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }

  // If the user is *not* authenticated, use this to get the ent profile.
  async getEntrepreneurProfileAnonymous(profileId: number, referralId?: string, originId?: string, permalinkId?: string): Promise<GetEntProfileResponse> {
    try {
      let ENDPOINT = `${API_URL}/entrepreneur/profile-public/${profileId}`;

      if (referralId) {
        const params = new URLSearchParams({ referralId });
        ENDPOINT += `?${params}`;
      } else if (originId) {
        const params = new URLSearchParams({ originId });
        ENDPOINT += `?${params}`;
      } else if (permalinkId) {
        const params = new URLSearchParams({ permalinkId });
        ENDPOINT += `?${params}`;
      }

      const response = await fetch(ENDPOINT, {
        method: 'GET'
      });

      const json = await response.json();

      if (!response.ok) {
        throw apiErrorToThrow(json);
      }

      const refData = json as GetEntProfileResponse;
      const { profile, referral } = refData;
      if (!profile) {
        console.error('Profile was not available on successful response from getEntAnonymous endpoint.');
        throw InternalError;
      }
      return { profile, referral };
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }

  async getMatchedInvestorsForEnt(auth0AccessToken: string, args: GetMatchedInvestorsForEntreprepeur): Promise<EntMatchedInvestorsResponse> {
    try {
      const { entId, query, body } = args;
      const ENDPOINT = `${API_URL}/entrepreneur/${entId}/matched-investors?${query}`;
      const response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { Authorization: `Bearer ${auth0AccessToken}` },
        body: JSON.stringify({ ...body })
      });

      const json = await response.json();

      if (!response.ok) {
        throw apiErrorToThrow(json);
      }

      return json as EntMatchedInvestorsResponse;
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }

  async getConnectorProfile(auth0AccessToken: string, profileId: number): Promise<ConnectorProfile> {
    try {
      const ENDPOINT = `${API_URL}/connector/profile/${profileId}`;
      const response = await fetch(ENDPOINT, {
        headers: {
          Authorization: `Bearer ${auth0AccessToken}`
        }
      });

      const json = await response.json();

      if (!response.ok) {
        throw apiErrorToThrow(json);
      }

      return json;
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }

  async getConnectorNetwork(auth0AccessToken: string, from: number, count: number, filters: FilterOption[]): Promise<ConnectorNetworkResponseType> {
    try {
      const filterQuery = filters?.map((i) => i.query).join('&');
      const ENDPOINT = `${API_URL}/connector/network?from=${from}&count=${count}&${filterQuery}`;
      const response = await fetch(ENDPOINT, {
        headers: {
          Authorization: `Bearer ${auth0AccessToken}`
        }
      });

      const json = await response.json();

      if (!response.ok) {
        throw apiErrorToThrow(json);
      }

      const { network, count: responseCount, from: responseFrom, total } = json;
      return { network, count: responseCount, from: responseFrom, total };
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }

  async getConnectorDeals(auth0AccessToken: string, status: ConnectorDealStatus, from: number, count: number): Promise<ConnectorDealsResponseType> {
    try {
      const ENDPOINT = `${API_URL}/connector/deals?status=${status}&from=${from}&count=${count}`;

      const response = await fetch(ENDPOINT, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${auth0AccessToken}`
        }
      });

      const json = await response.json();

      if (!response.ok) {
        throw apiErrorToThrow(json);
      }

      const { deals, matches, count: responseCount, from: responseFrom, total } = json;
      return { deals, matches, count: responseCount, from: responseFrom, total };
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }

  async getConnectorDealByEntProfile(auth0AccessToken: string, entProfileId: number): Promise<ConnectorDeal> {
    try {
      const ENDPOINT = `${API_URL}/connector/deals/profile/${entProfileId}`;

      const response = await fetch(ENDPOINT, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${auth0AccessToken}`
        }
      });

      const json = await response.json();

      if (!response.ok) {
        throw apiErrorToThrow(json);
      }

      return json;
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }

  async updateConnectorDeal(auth0AccessToken: string, id: number, body: UpdateConnectorDealBody): Promise<true> {
    try {
      const ENDPOINT = `${API_URL}/connector/deals/${id}`;

      const response = await fetch(ENDPOINT, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${auth0AccessToken}`
        },
        body: JSON.stringify(body)
      });

      const json = await response.json();

      if (!response.ok) {
        throw apiErrorToThrow(json);
      }

      return true;
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }

  async updateConnectorProfile(auth0AccessToken: string, body: any): Promise<ConnectorProfile> {
    try {
      const ENDPOINT = `${API_URL}/connector/profile`;

      const response = await fetch(ENDPOINT, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${auth0AccessToken}`
        },
        body: JSON.stringify(body)
      });

      const json = await response.json();

      if (!response.ok) {
        throw apiErrorToThrow(json);
      }

      return json;
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }

  async getInvestorProfile(auth0AccessToken: string, profileId: number): Promise<InvestorProfile> {
    try {
      const ENDPOINT = `${API_URL}/investor/profile/${profileId}`;

      const response = await fetch(ENDPOINT, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${auth0AccessToken}`
        }
      });

      const json = await response.json();

      if (!response.ok) {
        throw apiErrorToThrow(json);
      }

      return json;
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }

  async updateInvestorProfile(auth0AccessToken: string, networkId: number, body: InvestorUpdateBody): Promise<true> {
    try {
      const ENDPOINT = `${API_URL}/investor/profile/${networkId}`;

      const response = await fetch(ENDPOINT, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${auth0AccessToken}`
        },
        body: JSON.stringify(body)
      });

      const json = await response.json();

      if (!response.ok) {
        throw apiErrorToThrow(json);
      }

      return true;
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }

  async uploadImageAsset(auth0AccessToken: string, body:any): Promise<{ location: string }> {
    try {
      const ENDPOINT = `${API_URL}/user/image-asset`;

      const response = await fetch(ENDPOINT, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${auth0AccessToken}`,
          'Content-Type': body.mime
        },
        body: JSON.stringify(body)
      });

      const json = await response.json();

      if (!response.ok) {
        throw apiErrorToThrow(json);
      }

      return json;
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }

  async getUploadFileUrl(auth0AccessToken: string, fileName: string, fileExt: string): Promise<FileUploadUrl> {
    try {
      const ENDPOINT = `${API_URL}/user/file`;

      const response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${auth0AccessToken}`
        },
        body: JSON.stringify({ fileName, fileExt })
      });

      const json = await response.json();

      if (!response.ok) {
        throw apiErrorToThrow(json);
      }

      return json as FileUploadUrl;
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }

  async sendDealReferrals(auth0AccessToken: string, body: ConnectorSendReferralRequest): Promise<SendReferralsResponse> {
    try {
      const ENDPOINT = `${API_URL}/referrals/send`;

      const response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${auth0AccessToken}`
        },
        body: JSON.stringify(body)
      });

      const json = await response.json();

      if (!response.ok) {
        throw apiErrorToThrow(json);
      }

      return json as SendReferralsResponse;
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }

  async addInvestorToNetwork(auth0AccessToken: string, body: { email: string }): Promise<AddInvestorToNetworkResponse> {
    try {
      const ENDPOINT = `${API_URL}/connector/network`;

      const response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${auth0AccessToken}`
        },
        body: JSON.stringify(body)
      });

      const json = await response.json();

      if (!response.ok) {
        throw apiErrorToThrow(json);
      }

      return json as AddInvestorToNetworkResponse;
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }

  async sendAskReferrals(auth0AccessToken: string, body: EntrepreneurAskReferralRequest): Promise<true> {
    try {
      const ENDPOINT = `${API_URL}/referrals/ask`;

      const response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${auth0AccessToken}`
        },
        body: JSON.stringify(body)
      });

      const json = await response.json();

      if (!response.ok) {
        throw apiErrorToThrow(json);
      }

      return true;
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }

  async sendReferralReply(body: InvestorReferralReplyBody): Promise<true> {
    try {
      const ENDPOINT = `${API_URL}/referrals/reply`;

      const response = await fetch(ENDPOINT, {
        method: 'POST',
        body: JSON.stringify(body)
      });

      const json = await response.json();

      if (!response.ok) {
        throw apiErrorToThrow(json);
      }

      return true;
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }

  async getConnectorReferralOptions(auth0AccessToken: string, entId: number, connectorId?: number): Promise<ConnectorReferralOption[]> {
    try {
      const ENDPOINT = connectorId ? `${API_URL}/referrals/options?entId=${entId}&connectorId=${connectorId}` : `${API_URL}/referrals/options?entId=${entId}`;

      const response = await fetch(ENDPOINT, {
        method: 'GET',
        headers: { Authorization: `Bearer ${auth0AccessToken}` }
      });

      const json = await response.json();

      if (!response.ok) {
        throw apiErrorToThrow(json);
      }

      return json.referralOptions as ConnectorReferralOption[];
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }

  async sendPartnerReferrals(auth0AccessToken: string): Promise<true> {
    try {
      const ENDPOINT = `${API_URL}/corporate-referrals/send`;

      const response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${auth0AccessToken}`
        }
      });

      const json = await response.json();

      if (!response.ok) {
        throw apiErrorToThrow(json);
      }

      return true;
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }

  async sendEntInviteToJoin(auth0AccessToken: string, body: InviteEntRequestBody): Promise<InviteEntRequestResponse> {
    try {
      const ENDPOINT = `${API_URL}/connector/invite/ent`;

      const response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${auth0AccessToken}`
        },
        body: JSON.stringify(body)
      });

      const json = await response.json();

      if (!response.ok) {
        throw apiErrorToThrow(json);
      }
      return json as InviteEntRequestResponse;
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }

  async getReferralsFromPastDays(auth0AccessToken: string, days?: number | null): Promise<ReferralRequestResponse> {
    try {
      const args = days != null ? { daysBack: days } : {};
      const referrals = await getReferrals(auth0AccessToken, args) as ReferralRequestResponse;
      return referrals;
    } catch (error: unknown) {
      throw apiErrorToThrow(error);
    }
  }
}

const api = new API();
export default api;
